import { analyzeRequirements, criterionCoverage } from "./requirements";
import { parseToml } from "./toml";
import { bindCriterionTranslations, parseBilingualSidecars } from "./bilingualSidecar";
import {
  asNumber,
  basename,
  dirname,
  friendlyModel,
  joinPath,
  normalizePath,
  unique,
} from "./utils";

const safeJson = (entry, errors, label = entry?.path || "JSON") => {
  if (!entry?.text) return null;
  try {
    return JSON.parse(entry.text);
  } catch (error) {
    errors?.push(`${label}: ${error instanceof Error ? error.message : String(error)}`);
    return null;
  }
};

const allCriterionBlocks = (node, found = []) => {
  if (Array.isArray(node)) {
    for (const item of node) allCriterionBlocks(item, found);
    return found;
  }
  if (!node || typeof node !== "object") return found;
  if (Array.isArray(node.criteria)) found.push(node.criteria);
  for (const value of Object.values(node)) allCriterionBlocks(value, found);
  return found;
};

const criterionDetails = (entry) => {
  const errors = [];
  const json = safeJson(entry, errors);
  const seen = new Set();
  const details = [];
  for (const block of allCriterionBlocks(json)) {
    for (const item of block) {
      if (!item || typeof item !== "object") continue;
      const key = `${item.id || item.name}:${item.description || ""}:${item.value}`;
      if (seen.has(key)) continue;
      seen.add(key);
      details.push(item);
    }
  }
  return details;
};

const firstExisting = (byPath, candidates) => {
  for (const path of candidates) {
    const entry = byPath.get(normalizePath(path));
    if (entry) return entry;
  }
  return null;
};

const scoreFromSummary = (summary) => {
  const reward = summary?.verifier?.reward || {};
  const finalReward = asNumber(summary?.final_reward, asNumber(reward.reward));
  const gradedScore = asNumber(summary?.graded_score, asNumber(reward.graded_score));
  const effectiveScore = Math.max(finalReward ?? 0, gradedScore ?? 0);
  return {
    finalReward,
    gradedScore,
    reportedEffectiveScore: asNumber(summary?.effective_score),
    // Requirements defines the audit value, even when a stale summary field disagrees.
    effectiveScore,
    gating: asNumber(reward.gating, asNumber(summary?.gating)),
    verifierError: asNumber(reward.verifier_error, 0),
    gatingUnavailable: asNumber(reward.gating_unavailable, 0),
  };
};

const parseRun = (entry, byPath) => {
  const errors = [];
  const summary = safeJson(entry, errors);
  if (!summary || !summary.sample || summary.mode) return null;
  const base = dirname(entry.path);
  const trajectoryEntry = firstExisting(byPath, [
    joinPath(base, "agent/trajectory.json"),
    joinPath(base, "trajectory.json"),
  ]);
  const streamEntry = firstExisting(byPath, [
    joinPath(base, "agent/session_stream.jsonl"),
    joinPath(base, "agent/agent_logs/session_stream.jsonl"),
  ]);
  const gradedEntry = firstExisting(byPath, [
    joinPath(base, "verifier_replay/verifier_logs/graded/reward-details.json"),
    joinPath(base, "verifier/verifier_logs/graded/reward-details.json"),
  ]);
  const gateEntry = firstExisting(byPath, [
    joinPath(base, "verifier_replay/verifier_logs/gating/reward-details.json"),
    joinPath(base, "verifier/verifier_logs/gating/reward-details.json"),
  ]);
  const verificationEntry = firstExisting(byPath, [
    joinPath(base, "verifier_replay/verification.json"),
    joinPath(base, "verifier/verification.json"),
  ]);
  const trajectory = safeJson(trajectoryEntry, errors, trajectoryEntry?.path);
  const verification = safeJson(verificationEntry, errors, verificationEntry?.path);
  const scores = scoreFromSummary(summary);
  const gateDetails = criterionDetails(gateEntry);
  const failedGates = gateDetails.filter((item) => asNumber(item.value, 1) < 1);
  const modelLabel = friendlyModel(summary.solver_name, summary.model);
  return {
    id: `${summary.sample}:${summary.solver_name || basename(base)}`,
    sampleId: summary.sample,
    solver: summary.solver_name || basename(base),
    solverKind: summary.solver_kind || "",
    model: summary.model || "",
    modelLabel,
    status: summary.status || summary.agent?.status || "unknown",
    agentStatus: summary.agent?.status || summary.status || "unknown",
    agentExitCode: asNumber(summary.agent?.exit_code),
    passed: summary.passed === true,
    durationSec: asNumber(summary.duration_sec, asNumber(summary.agent?.duration_sec)),
    taskTomlSha: summary.task_toml_sha256 || "",
    rubricTreeSha: summary.rubric_tree_sha256 || "",
    taskTreeStable: summary.task_tree_stable,
    fresh: summary.task_tree_stable !== false,
    summary,
    verification,
    trajectory,
    trajectorySteps: Array.isArray(trajectory?.steps) ? trajectory.steps : [],
    streamText: streamEntry?.text || "",
    gradedDetails: criterionDetails(gradedEntry),
    gateDetails,
    failedGates,
    gateClipped:
      scores.gating === 0 &&
      scores.gradedScore !== null &&
      (scores.finalReward ?? 0) < scores.gradedScore,
    basePath: base,
    errors,
    ...scores,
  };
};

const parseControl = (entry) => {
  const errors = [];
  const summary = safeJson(entry, errors);
  if (!summary?.sample || !summary.mode) return null;
  const reward = summary?.verifier?.reward || {};
  return {
    sampleId: summary.sample,
    mode: summary.mode,
    reward: asNumber(summary.reward, asNumber(reward.reward)),
    gradedScore: asNumber(reward.graded_score),
    gating: asNumber(summary.gating, asNumber(reward.gating)),
    verifierError: asNumber(summary.verifier_error, asNumber(reward.verifier_error)),
    passed: summary.passed === true,
    path: entry.path,
    errors,
  };
};

const taskCandidateRank = (root, fileCount) => {
  let rank = fileCount;
  if (root.includes("examples/harbor_v3/coding/long_tail")) rank += 10000;
  if (root.includes("deliveries/")) rank += 1000;
  return rank;
};

const taskCandidates = (entries) => {
  const candidates = [];
  for (const entry of entries.filter((item) => basename(item.path) === "task.toml")) {
    const root = dirname(entry.path);
    const errors = [];
    const parsed = parseToml(entry.text || "", entry.path);
    if (parsed.error) errors.push(parsed.error);
    const id = parsed.value?.metadata?.task_id || basename(root);
    const files = entries
      .filter((item) => item.path === root || item.path.startsWith(`${root}/`))
      .map((item) => ({ ...item, relative: normalizePath(item.path.slice(root.length + 1)) }));
    candidates.push({ id, root, task: parsed.value || {}, errors, files });
  }
  return candidates;
};

const buildSample = async (candidate, runs, controls, sidecar) => {
  const files = candidate?.files || [];
  const byRelative = new Map(files.map((entry) => [entry.relative, entry]));
  const parseErrors = [...(candidate?.errors || [])];
  const instruction = byRelative.get("instruction.md")?.text || "";
  const judgeParsed = parseToml(
    byRelative.get("tests/graded/judge.toml")?.text || "",
    "tests/graded/judge.toml",
  );
  const gateParsed = parseToml(
    byRelative.get("tests/gating/gating.toml")?.text || "",
    "tests/gating/gating.toml",
  );
  if (judgeParsed.error) parseErrors.push(judgeParsed.error);
  if (gateParsed.error) parseErrors.push(gateParsed.error);

  const id = candidate?.id || runs[0]?.sampleId || controls[0]?.sampleId || "UNKNOWN";
  const analysis = analyzeRequirements({
    sampleId: id,
    files,
    instruction,
    task: candidate?.task || {},
    judge: judgeParsed.value || {},
    gating: gateParsed.value || {},
    parseErrors,
    runs,
  });
  const coverage = criterionCoverage(
    instruction,
    analysis.criteria.filter((item) => item.kind !== "programmatic"),
  );
  const task = candidate?.task || {};
  const metadata = task.metadata || {};
  const controlByMode = Object.fromEntries(controls.map((item) => [item.mode, item]));
  const translatedCriteria = await bindCriterionTranslations(
    id,
    analysis.criteria,
    sidecar,
  );
  return {
    id,
    root: candidate?.root || "",
    title: task.task?.description || instruction.match(/^#\s+(.+)$/m)?.[1] || id,
    version: task.task?.version || "—",
    difficulty: metadata.difficulty || "—",
    domain: metadata.domain || "—",
    category: [metadata.category_l1, metadata.category_l2, metadata.scene_l3].filter(Boolean),
    capabilities: metadata.capabilities || "",
    sourceNote: metadata.source_note || "",
    instruction,
    task,
    judge: judgeParsed.value || {},
    gating: gateParsed.value || {},
    files,
    byRelative,
    runs: [...runs].sort((a, b) => a.modelLabel.localeCompare(b.modelLabel)),
    controls: controlByMode,
    analysis: { ...analysis, criteria: translatedCriteria },
    coverage,
    parseErrors,
    sidecar: {
      files: sidecar?.files || 0,
      errors: sidecar?.errors || [],
      translationCount: [...(sidecar?.translations?.values?.() || [])].length,
    },
  };
};

export const parseWorkspace = async (rawEntries) => {
  const entries = rawEntries.map((entry) => ({ ...entry, path: normalizePath(entry.path) }));
  const byPath = new Map(entries.map((entry) => [entry.path, entry]));
  const sidecar = parseBilingualSidecars(entries);
  const candidates = taskCandidates(entries);
  const chosenById = new Map();
  for (const candidate of candidates) {
    const rank = taskCandidateRank(candidate.root, candidate.files.length);
    if (!chosenById.has(candidate.id) || chosenById.get(candidate.id).rank < rank) {
      chosenById.set(candidate.id, { candidate, rank });
    }
  }

  const runs = entries
    .filter((entry) => basename(entry.path) === "summary.json")
    .map((entry) => parseRun(entry, byPath))
    .filter(Boolean);
  const controls = entries
    .filter((entry) => basename(entry.path) === "summary.json")
    .map(parseControl)
    .filter(Boolean);
  const ids = unique([
    ...chosenById.keys(),
    ...runs.map((run) => run.sampleId),
    ...controls.map((control) => control.sampleId),
  ]).filter(Boolean);
  const samples = (await Promise.all(ids
    .map((id) =>
      buildSample(
        chosenById.get(id)?.candidate || null,
        runs.filter((run) => run.sampleId === id),
        controls.filter((control) => control.sampleId === id),
        sidecar,
      ),
    )))
    .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
  return {
    samples,
    entries,
    stats: {
      sampleCount: samples.length,
      runCount: runs.length,
      fileCount: entries.length,
      errors: samples.reduce((sum, sample) => sum + sample.analysis.errors, 0),
      warnings: samples.reduce((sum, sample) => sum + sample.analysis.warnings, 0),
    },
  };
};
